import { useEffect, useRef, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { Button, Input, toast } from '@/components/ui'
import {
  useAddSubscriber,
  useDeleteSubscriber,
  useReviewSettings,
  useSaveSettings,
  useSubscribers,
} from '../api/queries'
import type { SettingsValues } from '../api/types'

const LABELS: Record<string, string> = {
  history_days: 'History window (days)',
  active_min_orders: 'Active project: min orders in window',
  active_window_days: 'Active project: window (days)',
  active_last_order_within_days: 'Active project: last order within (days)',
  volume_window_days: 'Volume comparison window (days)',
  active_volume_ratio: 'Active project: recent vs prior volume (ratio)',
  accelerating_volume_ratio: 'Accelerating: recent vs prior volume (ratio)',
  winding_gap_multiplier: 'Winding down: gap over median gap (x)',
  winding_volume_drop: 'Winding down: volume drop (fraction)',
  sporadic_min_orders: 'Sporadic below this many orders',
  concentration_days: 'Concentration window (days)',
  concentration_flag: 'Project risk above top-customer share (fraction)',
  buy_buffer_days: 'Buy buffer (days)',
  default_lead_time_days: 'Default lead time (days)',
  blind_spot_demand_days: 'Blind spot: demand within (days)',
  observed_delay_receipts: 'Blind spot: receipts for observed delay',
  blanket_near_term_days: 'Blanket orders: near-term window (days)',
  quote_lookback_days: 'Quotes: look back (days)',
  quote_flag_days: 'Quotes exceed stock: only quotes from the last (days)',
  sample_code_prefixes: 'Sample code prefixes',
  sample_category_names: 'Sample categories',
  sample_picking_type_codes: 'Sample picking / warehouse codes',
  service_keywords: 'Service product keywords',
  excluded_detailed_types: 'Excluded product types',
  cache_ttl_seconds: 'Cache lifetime (seconds)',
  timezone: 'Company timezone',
  schedule_local_hour: 'Run hour (local)',
  schedule_weekday: 'Run weekday (0 = Monday)',
}

function toText(v: SettingsValues[string]): string {
  return Array.isArray(v) ? v.join(', ') : String(v)
}

/** Admin-only: thresholds (autosave on blur) and the DM subscriber list. */
export function ReviewSettingsPanel() {
  const settings = useReviewSettings()
  const save = useSaveSettings()
  const subs = useSubscribers(true)
  const addSub = useAddSubscriber()
  const delSub = useDeleteSubscriber()
  const [draft, setDraft] = useState<Record<string, string>>({})
  const seeded = useRef(false)
  const [email, setEmail] = useState('')

  // Seed the form once; refetches never overwrite what the admin is typing.
  useEffect(() => {
    if (seeded.current || !settings.data) return
    seeded.current = true
    setDraft(Object.fromEntries(Object.entries(settings.data.values).map(([k, v]) => [k, toText(v)])))
  }, [settings.data])

  function commit(key: string) {
    const defaults = settings.data?.defaults ?? {}
    const raw = draft[key] ?? ''
    const def = defaults[key]
    const value = Array.isArray(def)
      ? raw.split(',').map((x) => x.trim()).filter(Boolean)
      : typeof def === 'number'
        ? Number(raw)
        : raw
    if (toText(value as SettingsValues[string]) === toText(settings.data?.values[key] ?? '')) return
    save.mutate(
      { [key]: value } as SettingsValues,
      {
        onSuccess: () => toast.success(`Saved ${LABELS[key] ?? key}`),
        onError: (e) => toast.danger(`Not saved${e instanceof Error ? `: ${e.message}` : ''}`),
      },
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-3">
        <div>
          <h2 className="text-lg font-semibold text-primary">Thresholds and exclusions</h2>
          <p className="text-sm text-secondary">
            Changes save when you leave a field and apply from the next run. Lists are comma
            separated.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-x-6 gap-y-2 md:grid-cols-2">
          {Object.keys(settings.data?.defaults ?? {}).map((key) => (
            <label key={key} className="flex flex-col gap-1 text-sm">
              <span className="text-secondary">
                {LABELS[key] ?? key}
                <span className="ml-2 text-role-metadata">
                  default {toText(settings.data!.defaults[key])}
                </span>
              </span>
              <Input
                aria-label={LABELS[key] ?? key}
                value={draft[key] ?? ''}
                onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                onBlur={() => commit(key)}
              />
            </label>
          ))}
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <div>
          <h2 className="text-lg font-semibold text-primary">Monday Discuss DM</h2>
          <p className="text-sm text-secondary">
            Internal recipients of the weekly summary DM. Each must have an Odoo user and be on the
            CARLA DM allowlist.
          </p>
        </div>
        <ul className="flex flex-col gap-1">
          {(subs.data?.subscribers ?? []).map((s) => (
            <li key={s.id} className="flex items-center gap-2 text-sm">
              <span className="text-primary">{s.email}</span>
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                aria-label={`Remove ${s.email}`}
                onClick={() => delSub.mutate(s.id)}
              >
                <Trash2 size={12} />
              </Button>
            </li>
          ))}
        </ul>
        <div className="flex max-w-md gap-2">
          <Input
            aria-label="Subscriber email"
            placeholder="name@siliconesplus.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Button
            variant="primary"
            disabled={!email.trim()}
            loading={addSub.isPending}
            onClick={() =>
              addSub.mutate(email.trim(), {
                onSuccess: () => setEmail(''),
                onError: (e) => toast.danger(`Not added${e instanceof Error ? `: ${e.message}` : ''}`),
              })
            }
          >
            <Plus size={12} /> Add
          </Button>
        </div>
      </section>
    </div>
  )
}
